import PageTitle from "../../components/PageTitle";
import Button from "../../components/Button";
import Table from "../../components/Table/Table";
import { TableSearch } from "../../components/Table/TableActions";
import useWindowSize from "../../hooks/useWindow";
import { useState, useEffect } from "react";
import { httprequest } from "../../data/api";
import { Icon } from "@iconify/react/dist/iconify.js";
import { toast } from "react-toastify";
import PageLoader from "../../components/PageLoader";
import Modal from "../../components/Modal";
import Badge from "../../components/Badge";
import { useNavigate } from "react-router-dom";
import PopModal from "../../components/PopupModal/PopModal";

const SUPPORT_ENDPOINT = "/api/v1/tickets";
const DELETE_SUPPORT_ENDPOINT = "/api/v1/ticket";

const Support = () => {
  const navigate = useNavigate();
  const [currentRow, setCurrentRow] = useState({});
  const [supporttickets, setSupportTickets] = useState([]);
  const [searchResult, setSearchResult] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [isDeleting, setisDeleting] = useState(false);
  const width = useWindowSize();
  const mobile = width < 600;

  /**
 * Opens the delete confirmation modal for a specific ticket.
 * @param {Object} row - The ticket data to potentially delete.
 */
  const handleDeleteModal = (row) => {
    setCurrentRow(row);
    setDeleteModal(!deleteModal);
  };

  /**
 * Closes the delete confirmation modal.
 */
  const handleCloseDeleteModal = () => {
    setDeleteModal(false);
  };

  /**
 * Fetches all support tickets from the server.
 * @description Makes a GET request to SUPPORT_ENDPOINT and updates the tickets state.
 */
  const handleGetAllTickets = () => {
    setIsLoading(true);
    httprequest
      .get(SUPPORT_ENDPOINT)
      .then((res) => {
        setIsLoading(false);
        setSupportTickets(res?.data?.data);
        setSearchResult(res?.data?.data);
      })
      .catch((err) => {
        setIsLoading(false);
        toast.error(err?.response?.data?.message);
      });
  };
  /**
 * Deletes a support ticket.
 * @description Makes a DELETE request to remove the ticket and refreshes the tickets list.
 */
  const handleDeleteTicket = () => {
    setisDeleting(true);
    httprequest
      .delete(`${DELETE_SUPPORT_ENDPOINT}/${currentRow?.id}`)
      .then((res) => {
        setisDeleting(false);
        toast.success(res?.data?.message);
        handleCloseDeleteModal();
        handleGetAllTickets();
      })
      .catch((err) => {
        setisDeleting(false);
        toast.error(err?.response?.data?.message);
      });
  };

  /**
 * @effect
 * @description On component mount/Page load, fetches all support tickets.
 */
  useEffect(() => {
    handleGetAllTickets();
  }, []);

  /**
 * Column definitions for the support tickets data table.
 * @type {Array<Object>}
 * @property {string} name - The column header name.
 * @property {Function} selector - Function to render cell content.
 * @property {boolean} [center] - Whether to center the column content.
 * @property {Function} [cell] - Alternative render function for cells.
 * @property {boolean} [sortable] - Whether the column is sortable.
 */
  const columns = [
    {
      name: "Ticket ID",
      selector: (row) => (
        <>
          {mobile && <p className="mobile-styling">Title</p>}
          {row?.ticket_id || "--/--"}
        </>
      )
    },
    {
      name: "Subject",
      selector: (row) => (
        <>
          {mobile && <p className="mobile-styling">Added By</p>}
          {row?.subject || "--/--"}
        </>
      )
    },
    {
      name: "Priority",
      center: "true",
      cell: (row) => <Badge status={row?.priority} />
    },
    {
      name: "Status",
      selector: (row) => (
        <>
          {mobile && <p className="mobile-styling">Status</p>}
          {row?.status}
        </>
      ),
      center: "true"
    },
    {
      name: "Date Created",
      selector: (row) => (
        <>
          {mobile && <p className="mobile-styling">Date Created</p>}
          {row?.created_at?.substring(0, 10)}
        </>
      ),
      center: "true"
    },
    {
      name: "",
      center: "true",
      sortable: false,
      cell: (row) => (
        <div key={row?.id} className="table-action-container">
          <button className="table-action-icon" onClick={() => navigate(`/support/ticket/${row?.id}`)}>
            <Icon icon="fluent:eye-16-regular" />
          </button>
          <button
            className="table-action-icon delete-icon"
            onClick={() => handleDeleteModal(row)}
          >
            <Icon icon="mingcute:delete-2-line" />
          </button>
        </div>
      )
    }
  ];

  /**
 * Handles search functionality for support tickets.
 * @param {Object} e - The event object from the search input.
 * @description Filters tickets based on search terms matching status, subject, ticket_id, or priority.
 */
  const handleSearchSupport = (e) => {
    const { value } = e.target;

    const valArray = value.split(" ");
    // --| Filter data by partial match onchange in the search input box
    const result = supporttickets?.filter((data) => valArray?.every(
      (word) => data?.status?.toLowerCase().includes(word.toLowerCase()) ||
            data?.subject?.toLowerCase().includes(word.toLowerCase()) ||
            data?.ticket_id?.toLowerCase().includes(word.toLowerCase()) ||
            data?.priority?.toLowerCase().includes(word.toLowerCase())
    )
    );
    setSearchResult(result);
  };

  return (
    <>
      <PageTitle
        title="Tickets"
        subtitle="See all your tickets here"
        button={
          <div className="flex gap-10 button_tab">
            <PopModal title="Support" videoUrl="https://youtu.be/ETRWKKoIuIU" />
            <Button
              variant="primary"
              buttonSize="fluid"
              color="btndefault"
              onClick={() => navigate("/support/open-ticket")}
              buttonName="Create a Ticket"
            />

          </div>
        }
      />
      <div className="page-content">
        {isLoading ? (
          <PageLoader />
        ) : (
          <Table
            data={searchResult}
            columns={columns}
            subHeader={true}
            pagination
            subHeaderComponent={
              <TableSearch
                placeholder="Search here..."
                searchTableFunc={handleSearchSupport}
              />}
          />
        )}
      </div>
      {/* Delete Modal */}
      <Modal
        title="Delete A Ticket"
        subtitle={
          <div>
                Are you sure you want to remove TICKET {" "}
            <span className="modal-name-highlight">
              {currentRow?.ticket_id}
            </span>
            {" "} from one of your Tickets? Removing it will permanently
                delete this Ticket.
          </div>
        }
        modalContent={<></>}
        modalFooter={
          <>
            <div className="flex gap-10">
              {/* <Button
                variant="dangeroutline"
                buttonSize="full"
                color="btndanger"
                buttonName="Cancel"
                onClick={handleCloseDeleteModal}
              /> */}
              <Button
                variant="danger"
                buttonName="Delete Ticket"
                buttonSize="full"
                color="btndefault"
                onClick={handleDeleteTicket}
                isLoading={isDeleting}
              />
            </div>
          </>
        }
        isopen={deleteModal}
        closeModal={handleCloseDeleteModal}
      />
      {/* <ToastContainer /> */}
    </>
  );
};

export default Support;
